import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';
import { formatChip } from '../utils/formatTime';

const POLL_INTERVAL_MS = 4000;

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

const RECIPIENT_BADGE = {
  SENT: 'text-gray-600 bg-gray-100',
  WON: 'text-green-700 bg-green-100',
  MISSED: 'text-gray-400 bg-gray-50',
  SEND_FAILED: 'text-red-700 bg-red-100',
};

/**
 * Story 20-D — TODO(fast-follow): dismissing this modal (onClose) is the only way to leave it.
 * An OPEN, unclaimed offer becomes invisible from here — there's no "open offers" list on the
 * scheduler yet, so a forgotten unfilled offer can silently leave a shift uncovered. Filled
 * offers are fine (the assigned shift already shows on the grid via onClaimed→refetchShifts).
 * A future pass should add an open-offers indicator/badge to the toolbar.
 */
function OfferShiftStatusModal({ offerId, initialData, onClose, onClaimed }) {
  const { t, i18n } = useTranslation('scheduler');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const pollRef = useRef(null);
  const prevStatusRef = useRef(null);

  const fetchOnce = useCallback(() => {
    if (!offerId) return;
    apiClient
      .get(`/api/shift-offers/${offerId}`)
      .then((res) => {
        setData(res.data);
        setLoadError(false);
        if (res.data.status === 'CLAIMED' && prevStatusRef.current !== 'CLAIMED') {
          onClaimed?.();
        }
        prevStatusRef.current = res.data.status;
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [offerId, onClaimed]);

  // Story 20-D fix — this component is mounted once, unconditionally, by Scheduler (before any
  // offer exists) and reused across offers rather than remounted per offer. useState's
  // initial-value-from-props only ever ran at that first mount, with null props, so it never
  // reflected a real offer — `data`/`loading` must be re-seeded explicitly whenever `offerId`
  // changes rather than relying on the (non-reactive) useState initializer.
  useEffect(() => {
    if (!offerId) return;
    setLoadError(false);
    setCancelError('');
    prevStatusRef.current = initialData?.status ?? null;
    if (initialData) {
      setData(initialData);
      setLoading(false);
    } else {
      setData(null);
      setLoading(true);
      fetchOnce();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  useEffect(() => {
    if (!offerId || !data || data.status !== 'OPEN') {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [offerId, data, fetchOnce]);

  if (!offerId) return null;

  const handleCancel = async () => {
    if (!window.confirm(t('offerShift.status.cancelConfirm'))) return;
    setCancelling(true);
    setCancelError('');
    try {
      await apiClient.delete(`/api/shift-offers/${offerId}`);
      setData((prev) => (prev ? { ...prev, status: 'CANCELLED' } : prev));
    } catch (err) {
      setCancelError(err.response?.data?.message || t('offerShift.status.cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  const claimedRecipient = data?.recipients?.find((r) => r.employeeId === data.claimedByEmployeeId);

  const statusLabel = () => {
    switch (data?.status) {
      case 'OPEN':
        return t('offerShift.status.statusOpen');
      case 'CLAIMED':
        return claimedRecipient
          ? t('offerShift.status.statusClaimed', { name: claimedRecipient.employeeName })
          : t('offerShift.status.statusClaimedUnknown');
      case 'CANCELLED':
        return t('offerShift.status.statusCancelled');
      case 'EXPIRED':
        return t('offerShift.status.statusExpired');
      default:
        return '';
    }
  };

  const recipientLabel = (status) => {
    switch (status) {
      case 'SENT': return t('offerShift.status.recipientSent');
      case 'WON': return t('offerShift.status.recipientWon');
      case 'MISSED': return t('offerShift.status.recipientMissed');
      case 'SEND_FAILED': return t('offerShift.status.recipientSendFailed');
      default: return status;
    }
  };

  const summaryLine = data
    ? [
        data.roleLabel,
        new Date(`${data.shiftDate}T12:00:00`).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' }),
        data.startTime && data.endTime ? formatChip(data.startTime, data.endTime) : '',
      ].filter(Boolean).join(' · ')
    : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="offer-status-title"
    >
      <div className="w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-orange-100 flex flex-col overflow-hidden">
        <div className="shrink-0 flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 id="offer-status-title" className="text-lg font-semibold text-gray-900">
            {t('offerShift.status.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
            aria-label={t('offerShift.status.closeLabel')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-gray-500 gap-2">
              <Spinner />
            </div>
          ) : loadError && !data ? (
            <p className="text-sm text-red-600 text-center py-10">{t('offerShift.status.loadFailed')}</p>
          ) : data ? (
            <>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">{summaryLine}</p>
                <p className="text-sm text-gray-600 mt-1">{statusLabel()}</p>
              </div>

              {cancelError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                  {cancelError}
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  {t('offerShift.status.recipientsTitle')}
                </p>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {data.recipients?.map((r) => (
                    <div key={r.employeeId} className="flex items-center justify-between px-3 py-2.5">
                      <span className="text-sm text-gray-800 truncate">{r.employeeName}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${RECIPIENT_BADGE[r.recipientStatus] || 'text-gray-500 bg-gray-100'}`}>
                        {recipientLabel(r.recipientStatus)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>

        {data?.status === 'OPEN' && (
          <div className="shrink-0 flex items-center justify-end px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelling && <Spinner />}
              {cancelling ? t('offerShift.status.cancelling') : t('offerShift.status.cancelButton')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default OfferShiftStatusModal;
